import { fetchVideoMetadataAndNextResources } from "@redux/common/thunks";
import { isNotPremiumError } from "@redux/common/types";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { Song } from "src/@types/api-types";

import { playerInitialState } from "./constants";
import { type PartialSong, ScrollPosition } from "./types";

const playerSlice = createSlice({
	name: "player",
	initialState: playerInitialState,
	reducers: {
		initCastingSession: (state, action: PayloadAction<string>) => {
			try {
				const newState = JSON.parse(action.payload);
				state.currentSong = newState.currentSong;
				state.waitingList = [...newState.waitingList];
			} catch {
				console.log("Something went wrong while casting initialization.");
				console.log(`Could not parse player state : ${action.payload}`);
			}
		},
		saveCurrentSong: (state, action: PayloadAction<PartialSong | null>) => {
			state.currentSong = action.payload;
		},
		setCurrentSongEndTimestampInSecond: (
			state,
			action: PayloadAction<number>,
		) => {
			state.currentSongEndTimestampInSecond = action.payload;
		},
		saveLastSong: (state, action: PayloadAction<Song | null>) => {
			state.lastSong.song = action.payload;
			const song = action.payload;
			if (song) {
				const songIndex = state.waitingList.findIndex((s) => s.id === song.id);
				if (songIndex !== -1) {
					state.waitingList.splice(songIndex, 1);
				}
			}
		},
		disableFeatures: (state, action: PayloadAction<boolean>) => {
			state.lastSong.disableFeatures = action.payload;
		},
		pushToWaitingList: (state, action: PayloadAction<Song[]>) => {
			const songList = action.payload;
			state.waitingList = [
				...state.waitingList.filter(
					(song) => !songList.some((s) => s.id === song.id),
				),
				...songList,
			];

			state.waitingListPosition = ScrollPosition.BOTTOM;
		},
		pushToWaitingListAtPosition: (
			state,
			action: PayloadAction<{ song: Song; position: number }>,
		) => {
			const { song, position } = action.payload;
			state.waitingList.splice(position, 0, song);
		},

		pushTopToWaitingList: (state, action: PayloadAction<Song>) => {
			const song = action.payload;

			const songIndex = state.waitingList.findIndex((s) => s.id === song.id);
			if (songIndex !== -1) {
				state.waitingList.splice(songIndex, 1);
				state.waitingList.unshift(song);
			} else {
				state.waitingList.unshift(song);
			}

			state.waitingListPosition = ScrollPosition.TOP;
		},
		playNextSong: (state) => {
			const nextSong = state.waitingList.shift();
			state.currentSong = nextSong ? { ...nextSong } : null;
			state.waitingListPosition = ScrollPosition.TOP;
		},
		moveSongUp: (state, action: PayloadAction<number>) => {
			const songIndex = action.payload;
			[state.waitingList[songIndex], state.waitingList[songIndex - 1]] = [
				state.waitingList[songIndex - 1],
				state.waitingList[songIndex],
			];
		},
		moveSongDown: (state, action: PayloadAction<number>) => {
			const songIndex = action.payload;
			[state.waitingList[songIndex], state.waitingList[songIndex + 1]] = [
				state.waitingList[songIndex + 1],
				state.waitingList[songIndex],
			];
		},
		setVolumeBarDisplay: (state, action: PayloadAction<boolean>) => {
			state.volume.volumeBarDisplayed = action.payload;
		},
		configureVolumeMuteState: (state, action: PayloadAction<boolean>) => {
			state.volume.muted = action.payload;
		},
		deleteSong: (state, action: PayloadAction<number>) => {
			state.waitingList.splice(action.payload, 1);
		},
		setMusicPitchValue: (state, action: PayloadAction<number>) => {
			state.musicPitchValue = action.payload;
		},
		reorderWaitingList: (
			state,
			action: PayloadAction<{ songStartIndex: number; songEndIndex: number }>,
		) => {
			const { songStartIndex, songEndIndex } = action.payload;

			const [songToMove] = state.waitingList.splice(songStartIndex, 1);
			state.waitingList.splice(songEndIndex, 0, songToMove);
		},
		deleteAllSongs: (state) => {
			state.lastSong.song = null;
			state.waitingList = [];
		},
		setIsWaitingListSongDragging: (state, action: PayloadAction<boolean>) => {
			state.isWaitlingListSongDragging = action.payload;
		},
		setWaitingListSingerName: (
			state,
			action: PayloadAction<{ index: number; singerName: string }>,
		) => {
			const { index, singerName } = action.payload;
			state.waitingList[index].singerName = singerName;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(
			fetchVideoMetadataAndNextResources.rejected,
			(state, action) => {
				if (isNotPremiumError(action.payload)) {
					state.currentSong = null;
				}
			},
		);
	},
});

export const {
	disableFeatures,
	saveCurrentSong,
	saveLastSong,
	setCurrentSongEndTimestampInSecond,
	pushToWaitingList,
	pushTopToWaitingList,
	playNextSong,
	moveSongDown,
	moveSongUp,
	deleteSong,
	initCastingSession,
	reorderWaitingList,
	deleteAllSongs,
	setVolumeBarDisplay,
	configureVolumeMuteState,
	setIsWaitingListSongDragging,
	setWaitingListSingerName,
	setMusicPitchValue,
	pushToWaitingListAtPosition
} = playerSlice.actions;

export default playerSlice.reducer;
